import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getApplicationUser, updateApplicationUserPrivacyMode } from 'src/api/adminApi';
import { buildPrivacyFormatters, PrivacyFormatters } from 'src/utils/privacyMasking';

interface PrivacyModeContextType {
  privacyMode: boolean;
  isLoading: boolean;
  isReady: boolean;
  isSaving: boolean;
  updatePrivacyMode: (privacyMode: boolean) => Promise<void>;
}

// The default deliberately reads "off and ready" so a component rendered without the
// provider (every existing Jest suite, and any non-app-shell tree) renders real values.
const PrivacyModeContext = createContext<PrivacyModeContextType>({
  privacyMode: false,
  isLoading: false,
  isReady: true,
  isSaving: false,
  updatePrivacyMode: async () => {}
});

export const usePrivacyMode = () => useContext(PrivacyModeContext);

/**
 * Formatters for a component that opts into privacy masking at runtime.
 *
 * A component masks if and only if it calls this hook — the provider is a preference
 * provider, not a masking scope. Only call it from a component whose every consumer is an
 * in-scope surface (the user dashboard and the account dashboard).
 */
export const usePrivacyFormatters = (): PrivacyFormatters => {
  const { privacyMode } = usePrivacyMode();

  return useMemo(() => buildPrivacyFormatters(privacyMode), [privacyMode]);
};

export const PrivacyModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const [privacyMode, setPrivacyMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadPrivacyMode = async () => {
      if (isAuthLoading) {
        return;
      }

      if (!isAuthenticated) {
        setPrivacyMode(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const applicationUser = await getApplicationUser();
        setPrivacyMode(applicationUser.privacyMode === true);
      } catch (error) {
        console.error('Failed to load privacy mode preference:', error);
        setPrivacyMode(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadPrivacyMode();
  }, [isAuthenticated, isAuthLoading]);

  const updatePrivacyMode = async (nextPrivacyMode: boolean) => {
    const previousPrivacyMode = privacyMode;
    setPrivacyMode(nextPrivacyMode);
    setIsSaving(true);

    try {
      const applicationUser = await updateApplicationUserPrivacyMode(nextPrivacyMode);
      setPrivacyMode(applicationUser.privacyMode === true);
    } catch (error) {
      console.error('Failed to update privacy mode preference:', error);
      setPrivacyMode(previousPrivacyMode);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PrivacyModeContext.Provider
      value={{
        privacyMode,
        isLoading,
        isReady: !isLoading,
        isSaving,
        updatePrivacyMode
      }}
    >
      {children}
    </PrivacyModeContext.Provider>
  );
};
